import { Modal, ModalOverlay, ModalContent } from "@chakra-ui/react";
import { useWindowWidth } from "ui/utils/hooks";
import { BetaPill } from "ui/pages/app/components/activities/BetaPill";
import { translate } from "ui/i18n";
import { CloseButton } from "ui/pages/app/components/buttons/CloseButton";
import { HiArrowUp, HiCheck, HiX } from "react-icons/hi";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import {
  clearTechniqueFeedbackValues,
  closeAIFeedbackToast,
  getTechniquesByAISearch,
  openAIFeedbackToast,
  setAIToastStep,
  setTechniqueFeedbackValue,
} from "domain/ai-search/ai-search.actions";
import {
  selectFeedbackRequest,
  selectIsAISearchLoading,
} from "domain/ai-search/ai-search.selectors";
import { useNavigate } from "react-router-dom";
import LottieAnimation from "../../LottieAnimation";
import { Snackbar } from "ui/pages/snackbar";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  requestFeedback?: boolean;
  resetFilters?: () => void;
  redirect?: boolean;
}

export function ModalAISearch({
  isOpen,
  onClose,
  requestFeedback,
  resetFilters,
  redirect,
}: IProps) {
  const isDesktop = useWindowWidth();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsAISearchLoading);
  const savedAISearch = useAppSelector(selectFeedbackRequest);
  const [searchFocused, setSearchFocused] = useState(false);
  const [search, setSearch] = useState(savedAISearch ?? "");
  const [animationPart, setAnimationPart] = useState<"first" | "second">(
    "first"
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setSearchFocused(false);
      return;
    }

    setSearch(savedAISearch ?? "");
  }, [isOpen, savedAISearch]);

  useEffect(() => {
    if (requestFeedback) {
      dispatch(closeAIFeedbackToast());
      dispatch(setAIToastStep("first"));
    }
  }, [isOpen]); //eslint-disable-line

  useEffect(() => {
    if (isLoading) {
      setAnimationPart("first");
      setTimeout(() => {
        setAnimationPart("second");
      }, 5000);
    }
  }, [isLoading]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !isLoading) {
      e.preventDefault();
      onSubmit();
    }
  };

  const resetAiSearch = () => {
    dispatch(clearTechniqueFeedbackValues());
    setSearch("");
  };

  const onSubmit = async () => {
    const result = await dispatch(
      getTechniquesByAISearch({ search, limit: 8 })
    ).unwrap();
    if (result.success) {
      if (resetFilters) resetFilters();
      dispatch(setTechniqueFeedbackValue({ request: search }));
      setTimeout(() => {
        onClose();
      }, 1000);
      setSearchFocused(false);
      if (requestFeedback) {
        setTimeout(() => {
          dispatch(openAIFeedbackToast());
        }, 8000);
      }
      if (redirect) navigate("/techniques");
    } else if (result.quotaExceeded === true) {
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "ai-search");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={isLoading ? undefined : onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        maxW="750px"
        margin="20px"
        className="!rounded-[30px] p-6 md:p-8"
      >
        {!isDesktop && <CloseButton onClick={onClose} className="mb-6 w-fit" />}
        <div className="mb-4 flex items-center md:mb-6">
          <BetaPill className={isDesktop ? "me-4" : "me-3"} />
          <h3 className="text-base font-bold text-darkGrey-900 md:text-xl">
            {translate("ns1:AI.AISearch")}
          </h3>
        </div>
        <div style={{ maxWidth: 0, maxHeight: 0, overflow: "hidden" }}>
          <input autoFocus />
        </div>
        <div className="flex w-full rounded-xl border border-darkGrey-500 p-0 md:p-2">
          <textarea
            rows={isDesktop ? 4 : 5}
            placeholder={translate("ns1:AI.SearchPlaceholder")}
            onKeyDown={handleKeyDown}
            className="textarea-scroll w-full rounded-xl p-3 text-sm font-medium text-darkGrey-700 outline-none"
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            onFocus={() => setSearchFocused(true)}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={isLoading}
          />

          <div className="flex items-end">
            <div className="flex h-6 w-6 items-end justify-end md:h-10 md:w-10">
              {searchFocused && !isLoading && isDesktop && (
                <button
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-darkGrey-100 disabled:opacity-50"
                  onClick={onSubmit}
                  disabled={search === ""}
                >
                  <HiArrowUp className="text-darkGrey-800" size={20} />
                </button>
              )}
              {!searchFocused && !isLoading && (
                <button onClick={resetAiSearch} className="p-1 md:p-0">
                  <HiX className="text-darkGrey-800" size={24} />
                </button>
              )}
            </div>
          </div>
        </div>
        {!isLoading && (!savedAISearch || savedAISearch !== search) && (
          <div className="mt-4 flex flex-col rounded-[20px] bg-darkGrey-100 px-4 py-5 md:mt-6">
            {isDesktop ? (
              <>
                <span className="text-sm font-bold text-darkGrey-900 md:text-base">
                  {translate("ns1:AI.Ideas")}:
                </span>
                <ul className="mt-1 text-sm font-medium text-darkGrey-700">
                  <li>“{translate("ns1:AI.Idea1")}”</li>
                  <li>“{translate("ns1:AI.Idea2")}”</li>
                  <li>“{translate("ns1:AI.Idea3")}”</li>
                  <li>“{translate("ns1:AI.Idea4")}”</li>
                </ul>
              </>
            ) : (
              <>
                <span className="text-sm font-bold text-darkGrey-900 md:text-base">
                  {translate("ns1:AI.Idea")}:
                </span>
                <span className="mt-1 text-sm font-medium text-darkGrey-700">
                  “{translate("ns1:AI.MobileIdea")}”
                </span>
              </>
            )}
          </div>
        )}
        {isLoading && (
          <div className="mt-6 flex flex-col items-center justify-center md:mt-10">
            <LottieAnimation animation="aiLoader" />
            <p className="mt-4 text-sm md:text-base">
              {animationPart === "first"
                ? translate("ns1:AI.AnalyzingRequest")
                : translate("ns1:AI.GatheringResources")}
            </p>
          </div>
        )}
        {!isLoading && savedAISearch && savedAISearch === search && (
          <div className="mt-6 flex flex-col items-center justify-center md:mt-10">
            <HiCheck className="text-darkGrey-700" size={32} />
            <p className="mt-4 text-sm md:text-base">
              {translate("ns1:AI.EverythingReady")}
            </p>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
